import {
  useState, useEffect, useRef, useCallback, forwardRef,
  type ReactNode, type InputHTMLAttributes, type ElementType,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  signOut,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  type MultiFactorResolver,
  type User as FirebaseUser,
} from 'firebase/auth';
import QRCode from 'qrcode';
import { Infinity, Lock, Mail, ArrowRight, Shield, CheckCircle2, ScanLine, KeyRound, Smartphone, Phone, Fingerprint } from 'lucide-react';
import { loginWithPasskey, browserSupportsWebAuthn } from '@/lib/passkeys';

const LOGIN_RECAPTCHA_CONTAINER_ID = 'recaptcha-container-2fa-login';
import api from '@/lib/api';
import { firebaseAuth, googleProvider, microsoftProvider, isMobileAuthDevice } from '@/lib/firebase';
import { logLogin } from '@/lib/analytics';
import { useAuthStore } from '@/store/auth.store';

// Set right before signInWithRedirect so the post-redirect mount knows which
// provider to attribute the result (success, error, MFA) to.
const PENDING_OAUTH_PROVIDER_KEY = 'mobiuspo_pending_oauth_provider';

/* ─── keyframes ─── */
const STYLES = `
  @keyframes spin-border  { from{transform:translate(-50%,-50%) rotate(0deg)} to{transform:translate(-50%,-50%) rotate(360deg)} }
  @keyframes orbit-cw     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes orbit-ccw    { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes particle-rise{ 0%{transform:translateY(0) translateX(0);opacity:0} 15%{opacity:.8} 85%{opacity:.4} 100%{transform:translateY(-72px) translateX(12px);opacity:0} }
  @keyframes shimmer      { 0%{background-position:-300% center} 100%{background-position:300% center} }
  @keyframes pulse-dot    { 0%,100%{transform:scale(1);opacity:.6} 50%{transform:scale(1.4);opacity:1} }
  @keyframes ping         { 0%{transform:scale(1);opacity:.9} 100%{transform:scale(2.8);opacity:0} }
  @keyframes glow-breathe { 0%,100%{box-shadow:0 0 18px rgba(34,211,238,.25)} 50%{box-shadow:0 0 36px rgba(34,211,238,.55)} }
  @keyframes bar-grow     { from{transform:scaleY(0)} to{transform:scaleY(1)} }
  @keyframes fade-slide-up{ from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes progress-bar { from{width:0%} to{width:100%} }
  @keyframes scan-h       { 0%{top:0%;opacity:.8} 100%{top:100%;opacity:0} }
`;

/* ─── schema ─── */
const schema = z.object({
  email:    z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});
type FormData = z.infer<typeof schema>;

/* ─── particles ─── */
const PARTICLES = [
  { l:'8%',  t:'14%', s:2,   d:'0s',   dur:'6s'  },
  { l:'82%', t:'38%', s:1.5, d:'1.4s', dur:'7s'  },
  { l:'31%', t:'68%', s:2.5, d:'0.6s', dur:'8s'  },
  { l:'91%', t:'12%', s:1,   d:'2.2s', dur:'5.5s'},
  { l:'55%', t:'82%', s:2,   d:'1.9s', dur:'9s'  },
  { l:'18%', t:'52%', s:1.5, d:'3.1s', dur:'6.5s'},
  { l:'70%', t:'28%', s:1,   d:'0.3s', dur:'7.5s'},
  { l:'44%', t:'46%', s:2,   d:'2.7s', dur:'6s'  },
];

/* ═══════════════════════════════════════════
   SPLASH SCREENS
═══════════════════════════════════════════ */

/* ─── Screen 1: Payment Routing Flow ─── */
const GATEWAYS = [
  { name:'UPI',        y:38,  color:'#818cf8' },
  { name:'Cards',      y:100, color:'#22d3ee' },
  { name:'NetBanking', y:162, color:'#a855f7' },
  { name:'Wallets',    y:224, color:'#34d399' },
  { name:'BNPL',       y:284, color:'#fb923c' },
];

function Screen1() {
  return (
    <div className="w-full h-full flex flex-col">
      <svg viewBox="0 0 460 322" style={{ width:'100%', height:'100%', flex:1 }}>
        <defs>
          <filter id="s1-glow"><feGaussianBlur stdDeviation="3.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          <filter id="s1-glow-lg"><feGaussianBlur stdDeviation="6" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          <radialGradient id="s1-hub"><stop offset="0%" stopColor="#22d3ee" stopOpacity=".22"/><stop offset="100%" stopColor="#22d3ee" stopOpacity="0"/></radialGradient>
          <radialGradient id="s1-app"><stop offset="0%" stopColor="#22d3ee" stopOpacity=".08"/><stop offset="100%" stopColor="#22d3ee" stopOpacity="0"/></radialGradient>
        </defs>

        {/* YOUR APP node */}
        <circle cx="68" cy="161" r="72" fill="url(#s1-app)"/>
        <circle cx="68" cy="161" r="48" fill="rgba(34,211,238,.05)" stroke="#22d3ee" strokeWidth="1.6" filter="url(#s1-glow)"/>
        <circle cx="68" cy="161" r="34" fill="none" stroke="rgba(34,211,238,.18)" strokeWidth="1"/>
        <text x="68" y="154" textAnchor="middle" fill="#22d3ee" fontSize="11" fontWeight="900" letterSpacing="1.2">YOUR</text>
        <text x="68" y="172" textAnchor="middle" fill="#22d3ee" fontSize="11" fontWeight="900" letterSpacing="1.2">APP</text>

        {/* Input connector line */}
        <path d="M 118 161 L 190 161" stroke="rgba(34,211,238,.4)" strokeWidth="1.6" strokeDasharray="6 5"/>
        <circle r="4.5" fill="#22d3ee" filter="url(#s1-glow)"><animateMotion path="M 118 161 L 190 161" dur="1.6s" repeatCount="indefinite"/></circle>
        <circle r="4.5" fill="#22d3ee" filter="url(#s1-glow)" opacity=".55"><animateMotion path="M 118 161 L 190 161" dur="1.6s" begin="0.8s" repeatCount="indefinite"/></circle>

        {/* MOBIUS hub — aura */}
        <circle cx="240" cy="161" r="90" fill="url(#s1-hub)">
          <animate attributeName="r" values="82;96;82" dur="3.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values=".5;1;.5" dur="3.5s" repeatCount="indefinite"/>
        </circle>
        {/* Orbit rings */}
        <circle cx="240" cy="161" r="68" fill="none" stroke="rgba(34,211,238,.16)" strokeWidth="1" strokeDasharray="10 7">
          <animateTransform attributeName="transform" type="rotate" from="0 240 161" to="360 240 161" dur="9s" repeatCount="indefinite"/>
        </circle>
        <circle cx="240" cy="161" r="56" fill="none" stroke="rgba(168,85,247,.14)" strokeWidth="1" strokeDasharray="5 9">
          <animateTransform attributeName="transform" type="rotate" from="0 240 161" to="-360 240 161" dur="14s" repeatCount="indefinite"/>
        </circle>
        <circle cx="240" cy="161" r="44" fill="none" stroke="rgba(34,211,238,.1)" strokeWidth=".8" strokeDasharray="3 12">
          <animateTransform attributeName="transform" type="rotate" from="0 240 161" to="360 240 161" dur="20s" repeatCount="indefinite"/>
        </circle>
        {/* Hub core */}
        <circle cx="240" cy="161" r="38" fill="rgba(34,211,238,.07)" stroke="rgba(34,211,238,.65)" strokeWidth="1.8" filter="url(#s1-glow-lg)"/>
        <text x="240" y="149" textAnchor="middle" fill="#22d3ee" fontSize="22" fontWeight="900" filter="url(#s1-glow)">∞</text>
        <text x="240" y="163" textAnchor="middle" fill="rgba(34,211,238,.85)" fontSize="7.5" fontWeight="900" letterSpacing="2.5">SPX</text>
        <text x="240" y="174" textAnchor="middle" fill="rgba(34,211,238,.5)" fontSize="4.8" fontWeight="700" letterSpacing="1.8">SMART ROUTING</text>

        {/* Gateway paths + nodes */}
        {GATEWAYS.map((gw, i) => {
          const path = `M 280 161 Q 355 161 418 ${gw.y}`;
          return (
            <g key={gw.name}>
              <path d={path} fill="none" stroke={gw.color} strokeWidth="1.1" strokeOpacity=".3" strokeDasharray="6 5"/>
              <circle r="3.8" fill={gw.color} filter="url(#s1-glow)">
                <animateMotion path={path} dur={`${1.3 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.35}s`}/>
              </circle>
              {/* Gateway node */}
              <circle cx="418" cy={gw.y} r="22" fill={`${gw.color}14`} stroke={gw.color} strokeWidth="1.4" strokeOpacity=".7" filter="url(#s1-glow)"/>
              <circle cx="418" cy={gw.y} r="14" fill={`${gw.color}0a`} stroke={gw.color} strokeWidth=".7" strokeOpacity=".3"/>
              <text x="418" y={gw.y + 4} textAnchor="middle" fill={gw.color} fontSize="7.5" fontWeight="800">{gw.name}</text>
              {/* Live status dot */}
              <circle cx="436" cy={gw.y - 17} r="3.5" fill="#34d399" filter="url(#s1-glow)">
                <animate attributeName="opacity" values="1;.25;1" dur={`${1.2 + i * 0.25}s`} repeatCount="indefinite"/>
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ─── Screen 2: Real-Time Analytics ─── */
const HOURS = ['00','02','04','06','08','10','12','14','16','18','20','22'];
const BAR_H  = [42,58,35,72,65,80,48,91,74,88,62,95];
const LINE_Y = BAR_H.map(h => 150 - h * 1.3);

function Screen2() {
  return (
    <div className="w-full flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label:'Today Revenue', val:'₹2.4Cr',  color:'#a855f7' },
          { label:'Success Rate',  val:'98.7%',   color:'#34d399' },
          { label:'Transactions',  val:'4,821',   color:'#60a5fa' },
        ].map((m) => (
          <div key={m.label} className="rounded-xl p-3 text-center" style={{ background:`${m.color}0d`, border:`1px solid ${m.color}28` }}>
            <p style={{ color:m.color, fontSize:18, fontWeight:900, lineHeight:1.1 }}>{m.val}</p>
            <p style={{ color:'#1e3a4a', fontSize:8, textTransform:'uppercase', letterSpacing:'0.09em', marginTop:3 }}>{m.label}</p>
          </div>
        ))}
      </div>
      <svg viewBox="0 0 360 170" style={{ width:'100%' }}>
        <defs>
          <linearGradient id="s2-bar0" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity=".85"/>
            <stop offset="100%" stopColor="#a855f7" stopOpacity=".15"/>
          </linearGradient>
          <linearGradient id="s2-bar1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity=".85"/>
            <stop offset="100%" stopColor="#7c3aed" stopOpacity=".15"/>
          </linearGradient>
          <filter id="s2-glow"><feGaussianBlur stdDeviation="1.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        {/* Grid lines */}
        {[0,40,80,120].map(y => (
          <line key={y} x1="0" y1={y+5} x2="360" y2={y+5} stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
        ))}
        {/* Bars */}
        {BAR_H.map((h, i) => {
          const barH = h * 1.3;
          const x = i * 29 + 7;
          return (
            <rect key={i} x={x} y={155 - barH} width={20} height={barH} fill={`url(#s2-bar${i % 2})`} rx="3">
              <animate attributeName="height" from="0" to={barH} dur="1.2s" begin={`${i * 0.08}s`} fill="freeze"/>
              <animate attributeName="y" from="155" to={155 - barH} dur="1.2s" begin={`${i * 0.08}s`} fill="freeze"/>
            </rect>
          );
        })}
        {/* Success-rate line */}
        <polyline
          points={LINE_Y.map((y, i) => `${i * 29 + 17},${y}`).join(' ')}
          fill="none" stroke="#34d399" strokeWidth="1.5" strokeOpacity=".65" filter="url(#s2-glow)" strokeDasharray="4 2"
        />
        {LINE_Y.map((y, i) => (
          <circle key={i} cx={i * 29 + 17} cy={y} r="2" fill="#34d399" filter="url(#s2-glow)"/>
        ))}
        {/* X-axis labels */}
        {HOURS.map((h, i) => (
          <text key={h} x={i * 29 + 17} y="168" textAnchor="middle" fill="rgba(255,255,255,0.15)" fontSize="7">{h}h</text>
        ))}
      </svg>
    </div>
  );
}

/* ─── Screen 3: Gateway Health Monitor ─── */
const GW_HEALTH = [
  { name:'Gateway 01', uptime:99.9, latency:87,  success:99.2, ok:true  },
  { name:'Gateway 02', uptime:99.7, latency:112, success:98.8, ok:true  },
  { name:'Gateway 03', uptime:98.5, latency:145, success:97.4, ok:false },
  { name:'Gateway 04', uptime:99.8, latency:95,  success:99.1, ok:true  },
  { name:'Gateway 05', uptime:97.2, latency:198, success:96.8, ok:false },
];

function Screen3() {
  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color:'#2d4a5f' }}>Live health status across all connected payment providers.</p>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.2)' }}>
          <span style={{ width:5, height:5, borderRadius:'50%', background:'#34d399', display:'inline-block', animation:'pulse-dot 2s infinite' }}/>
          <span style={{ color:'#34d399', fontSize:8, fontWeight:700, letterSpacing:'0.1em' }}>LIVE</span>
        </div>
      </div>
      {/* Column headers */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 52px 58px 52px', gap:'0 6px', padding:'0 10px', marginBottom:2 }}>
        {['Gateway','Uptime','Latency','Success'].map(h => (
          <span key={h} style={{ color:'#1e3344', fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', textAlign: h==='Gateway' ? 'left':'right' }}>{h}</span>
        ))}
      </div>
      {/* Rows */}
      <div className="flex flex-col gap-2">
        {GW_HEALTH.map((gw, i) => (
          <div key={gw.name} style={{ display:'grid', gridTemplateColumns:'1fr 52px 58px 52px', gap:'0 6px', alignItems:'center', padding:'9px 10px', borderRadius:10, background:'rgba(255,255,255,0.03)', border:`1px solid ${gw.ok ? 'rgba(52,211,153,0.12)' : 'rgba(251,146,60,0.12)'}`, animation:`fade-slide-up 0.4s ${i*0.08}s both` }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background: gw.ok ? '#34d399' : '#f59e0b', display:'inline-block', flexShrink:0, animation:'pulse-dot 2s infinite' }}/>
              <span style={{ color:'#94a3b8', fontSize:11, fontWeight:600 }}>{gw.name}</span>
            </div>
            <span style={{ color:'#34d399', fontSize:11, fontWeight:700, textAlign:'right' }}>{gw.uptime}%</span>
            <span style={{ color: gw.latency < 130 ? '#60a5fa' : '#f59e0b', fontSize:11, fontWeight:700, textAlign:'right' }}>{gw.latency}ms</span>
            <span style={{ color:'#a855f7', fontSize:11, fontWeight:700, textAlign:'right' }}>{gw.success}%</span>
          </div>
        ))}
      </div>
      {/* Uptime bar summary */}
      <div className="mt-1 flex flex-col gap-1.5">
        {GW_HEALTH.slice(0,3).map(gw => (
          <div key={gw.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width:44, color:'#1e3a4a', fontSize:8, textAlign:'right', flexShrink:0 }}>{gw.name}</span>
            <div style={{ flex:1, height:4, borderRadius:4, background:'rgba(255,255,255,0.06)', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${gw.uptime}%`, background: gw.ok ? 'linear-gradient(90deg,#22d3ee,#34d399)' : 'linear-gradient(90deg,#f59e0b,#fb923c)', borderRadius:4, animation:`progress-bar 1.2s ease-out both` }}/>
            </div>
            <span style={{ width:36, color:'#34d399', fontSize:8, fontWeight:700, flexShrink:0 }}>{gw.uptime}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Screen 4: AI Routing Engine ─── */
const AI_STEPS = [
  { s:'Parsing transaction attributes…',         color:'#fb923c' },
  { s:'Scoring 10 gateway profiles…',            color:'#fb923c' },
  { s:'Applying geo & method heuristics…',       color:'#fb923c' },
  { s:'Checking real-time latency thresholds…',  color:'#fb923c' },
  { s:'✓  Optimal gateway selected',             color:'#34d399' },
];

function Screen4() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    let s = 0;
    const t = setInterval(() => {
      s += 1;
      if (s > AI_STEPS.length) { s = 0; setStep(0); }
      else setStep(s);
    }, 950);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* Input transaction */}
      <div style={{ borderRadius:14, padding:'14px 18px', background:'rgba(251,146,60,0.07)', border:'1px solid rgba(251,146,60,0.26)' }}>
        <p style={{ color:'rgba(251,146,60,0.55)', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.14em', marginBottom:10 }}>Incoming Transaction</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
          {[['Amount','₹5,000'],['Country','India'],['Method','Visa CC'],['3DS','Required']].map(([l,v]) => (
            <div key={l}>
              <p style={{ color:'#1e3a4a', fontSize:8.5, textTransform:'uppercase', letterSpacing:'0.07em' }}>{l}</p>
              <p style={{ color:'#e2e8f0', fontSize:14, fontWeight:800, marginTop:3 }}>{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* AI processing steps — flex-1 fills remaining space */}
      <div style={{ flex:1, borderRadius:14, padding:'16px 18px', background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.2)', display:'flex', flexDirection:'column' }}>
        <p style={{ color:'rgba(168,85,247,0.55)', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.14em', marginBottom:14 }}>AI Routing Engine</p>
        <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'space-around' }}>
          {AI_STEPS.map((s2, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ width:9, height:9, borderRadius:'50%', background: i < step ? '#34d399' : i === step ? s2.color : 'rgba(255,255,255,0.09)', display:'inline-block', flexShrink:0, transition:'background 0.3s', boxShadow: i < step ? '0 0 8px #34d399' : i === step ? `0 0 10px ${s2.color}` : 'none' }}/>
              <span style={{ fontSize:12.5, transition:'color 0.3s', color: i < step ? '#34d399' : i === step ? '#e2e8f0' : '#1e3344', fontWeight: i === step ? 600 : 400 }}>{s2.s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Result */}
      <div style={{ borderRadius:14, padding:'14px 18px', background: step >= AI_STEPS.length ? 'rgba(52,211,153,0.09)' : 'rgba(52,211,153,0.03)', border:`1px solid ${step >= AI_STEPS.length ? 'rgba(52,211,153,0.4)' : 'rgba(52,211,153,0.1)'}`, transition:'all 0.5s' }}>
        <p style={{ color:'rgba(52,211,153,0.45)', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.14em', marginBottom:5 }}>Decision</p>
        <p style={{ color: step >= AI_STEPS.length ? '#34d399' : '#1e3344', fontSize:20, fontWeight:900, transition:'color 0.5s', letterSpacing:'-0.02em' }}>Route → Gateway 01</p>
        <p style={{ color: step >= AI_STEPS.length ? '#2d5a4a' : '#111d28', fontSize:10.5, marginTop:4, transition:'color 0.5s' }}>98.9% success rate · 112ms avg · ₹0.02 lower fee vs. next best</p>
      </div>
    </div>
  );
}

/* ─── Screen 5: Pan-India Network ─── */
/*
 * Coordinate system (counterclockwise, west coast first):
 *   x = 78 + (lon − 68.5) × 12.4
 *   y = 385 − (lat − 8)   × 12.9
 * Range: x 78–431 (68.5°E–97°E), y 21–384 (36.2°N–8°N)
 */
const INDIA_PATH = `
  M 190 384
  L 177 371 L 163 345 L 150 306 L 141 286
  L 131 242 L 127 217
  L 96 198 L 78 179
  L 96 140 L 134 88 L 152 56
  L 140 30 L 164 21
  L 196 30 L 246 50
  L 227 114 L 270 114 L 320 127
  L 357 140 L 388 140 L 431 133
  L 407 153 L 388 166 L 376 179
  L 357 185 L 338 192 L 326 198
  L 314 204 L 305 217
  L 293 230 L 283 237
  L 264 256 L 252 262 L 233 282
  L 221 295 L 224 319 L 221 333
  L 218 346 L 208 366
  Z
`;

const INDIA_CITIES = [
  { name:'Mumbai',    x:131, y:242, c:'#22d3ee', d:'0s'    },
  { name:'Delhi',     x:186, y:119, c:'#60a5fa', d:'0.5s'  },
  { name:'Bengaluru', x:191, y:322, c:'#a855f7', d:'1.0s'  },
  { name:'Hyderabad', x:202, y:264, c:'#fb923c', d:'1.5s'  },
  { name:'Chennai',   x:224, y:319, c:'#f472b6', d:'0.8s'  },
  { name:'Kolkata',   x:325, y:197, c:'#34d399', d:'1.3s'  },
  { name:'Pune',      x:145, y:250, c:'#fbbf24', d:'1.8s'  },
];

const INDIA_METHODS = [
  { l:'UPI',        c:'#818cf8' },
  { l:'Cards',      c:'#22d3ee' },
  { l:'NetBanking', c:'#a855f7' },
  { l:'Wallets',    c:'#34d399' },
  { l:'BNPL',       c:'#fb923c' },
];

function Screen5() {
  return (
    <div className="w-full h-full flex flex-col" style={{ minHeight: 0 }}>
      <svg viewBox="0 0 460 422" style={{ width: '100%', flex: 1, minHeight: 0 }}>
        <defs>
          <filter id="s5-glow">
            <feGaussianBlur stdDeviation="2.8" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="s5-blob">
            <feGaussianBlur stdDeviation="9" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id="s5-fill" cx="42%" cy="52%" r="50%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity=".24"/>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0"/>
          </radialGradient>
          <linearGradient id="s5-scan" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#60a5fa" stopOpacity="0"/>
            <stop offset="50%"  stopColor="#60a5fa" stopOpacity=".52"/>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0"/>
          </linearGradient>
          <clipPath id="s5-india-clip">
            <path d={INDIA_PATH} transform="translate(14,57) scale(0.85,0.85)"/>
          </clipPath>
        </defs>

        {/* Background dot grid */}
        {Array.from({ length: 21 }, (_, r) =>
          Array.from({ length: 23 }, (_, c) => (
            <circle key={`${r}-${c}`} cx={c * 20 + 10} cy={r * 20 + 10} r=".5" fill="rgba(255,255,255,0.022)"/>
          ))
        )}

        {/* ── Stats strip (top) ── */}
        {([
          { val:'14Bn+', label:'UPI Txns/mo', c:'#60a5fa', cx:77  },
          { val:'28+',   label:'States',       c:'#34d399', cx:230 },
          { val:'99.1%', label:'Uptime SLA',   c:'#a855f7', cx:383 },
        ] as const).map(s => (
          <g key={s.label}>
            <rect x={s.cx - 57} y={4} width={114} height={46} rx="8"
              fill={`${s.c}0e`} stroke={`${s.c}32`} strokeWidth="1"/>
            <text x={s.cx} y={28} textAnchor="middle" fill={s.c} fontSize="19" fontWeight="900">{s.val}</text>
            <text x={s.cx} y={43} textAnchor="middle" fill={s.c} fontSize="7.2" fontWeight="700" opacity=".72">{s.label.toUpperCase()}</text>
          </g>
        ))}

        {/* ── India map group — scaled & shifted to fill remaining height ── */}
        <g transform="translate(14,57) scale(0.85,0.85)">
          {/* Outer ambient glow blob */}
          <path d={INDIA_PATH} fill="rgba(96,165,250,0.09)" filter="url(#s5-blob)"/>
          {/* Landmass gradient fill */}
          <path d={INDIA_PATH} fill="url(#s5-fill)"/>
          {/* Border */}
          <path d={INDIA_PATH}
            fill="rgba(96,165,250,0.06)"
            stroke="rgba(96,165,250,0.62)"
            strokeWidth="1.9"
            strokeLinejoin="round"
            filter="url(#s5-glow)"
          />

          {/* Animated dashed connection lines from Mumbai */}
          {INDIA_CITIES.slice(1).map(city => (
            <line key={city.name}
              x1={INDIA_CITIES[0].x} y1={INDIA_CITIES[0].y}
              x2={city.x} y2={city.y}
              stroke={city.c} strokeWidth="1.1" strokeOpacity=".38" strokeDasharray="5 7"
            >
              <animate attributeName="stroke-dashoffset" values="0;-120" dur="2.8s" repeatCount="indefinite"/>
            </line>
          ))}

          {/* Data packets flowing along connections */}
          {INDIA_CITIES.slice(1).map((city, i) => (
            <circle key={`pkt-${i}`} r="2.6" fill={city.c} opacity=".9">
              <animateMotion
                path={`M ${INDIA_CITIES[0].x} ${INDIA_CITIES[0].y} L ${city.x} ${city.y}`}
                dur={`${1.6 + i * 0.28}s`}
                repeatCount="indefinite"
                begin={city.d}
              />
            </circle>
          ))}

          {/* City pings */}
          {INDIA_CITIES.map(city => (
            <g key={city.name}>
              <circle cx={city.x} cy={city.y} r="4" fill="none" stroke={city.c} strokeWidth="1.3">
                <animate attributeName="r"       values="4;22;4" dur="2.8s" repeatCount="indefinite" begin={city.d}/>
                <animate attributeName="opacity" values=".9;0;.9" dur="2.8s" repeatCount="indefinite" begin={city.d}/>
              </circle>
              <circle cx={city.x} cy={city.y} r="4" fill={city.c} filter="url(#s5-glow)"/>
              <text x={city.x + 7} y={city.y - 4} fill={city.c} fontSize="8" fontWeight="700" opacity=".92">{city.name}</text>
            </g>
          ))}

          {/* ₹ watermark */}
          <text x="252" y="222" textAnchor="middle" fill="rgba(96,165,250,0.052)" fontSize="62" fontWeight="900">₹</text>
        </g>

        {/* Scan line sweeping through India */}
        <rect x="0" y="0" width="460" height="34" fill="url(#s5-scan)" clipPath="url(#s5-india-clip)" opacity=".75">
          <animateTransform attributeName="transform" type="translate"
            from="0 57" to="0 392" dur="3.6s" repeatCount="indefinite"/>
        </rect>

        {/* ── Payment method pills (bottom) ── */}
        {INDIA_METHODS.map((m, i) => {
          const pillW = 72, gap = 10;
          const total = INDIA_METHODS.length * pillW + (INDIA_METHODS.length - 1) * gap;
          const startX = (460 - total) / 2;
          const px = startX + i * (pillW + gap);
          return (
            <g key={m.l}>
              <rect x={px} y={400} width={pillW} height={18} rx="6"
                fill={`${m.c}12`} stroke={`${m.c}34`} strokeWidth="1"/>
              <text x={px + pillW / 2} y={412.5} textAnchor="middle"
                fill={m.c} fontSize="8.5" fontWeight="700">{m.l}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ─── Screen registry ─── */
const SCREENS = [
  { id:'routing',   title:'Smart Routing',     accent:'#22d3ee', Comp:Screen1 },
  { id:'analytics', title:'Real-Time Analytics', accent:'#a855f7', Comp:Screen2 },
  { id:'health',    title:'Gateway Health',    accent:'#34d399', Comp:Screen3 },
  { id:'ai',        title:'AI Engine',         accent:'#fb923c', Comp:Screen4 },
  { id:'india',     title:'Pan-India Network',  accent:'#60a5fa', Comp:Screen5 },
];

/* ═══════════════════════════════════════════
   SPLASH PANEL
═══════════════════════════════════════════ */
function SplashPanel() {
  const [active, setActive]   = useState(0);
  const [fading, setFading]   = useState(false);
  const activeRef             = useRef(0);
  const fadingRef             = useRef(false);
  const INTERVAL_MS           = 5200;

  const goTo = useCallback((i: number) => {
    if (i === activeRef.current || fadingRef.current) return;
    fadingRef.current = true;
    setFading(true);
    setTimeout(() => {
      activeRef.current = i;
      setActive(i);
      fadingRef.current = false;
      setFading(false);
    }, 320);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      goTo((activeRef.current + 1) % SCREENS.length);
    }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [goTo]);

  const current = SCREENS[active];

  return (
    <div className="h-full relative overflow-hidden flex flex-col"
      style={{ background:'linear-gradient(155deg,#040a18 0%,#070f22 55%,#040c18 100%)' }}>

      {/* Grid */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage:'linear-gradient(rgba(34,211,238,.032) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,.032) 1px,transparent 1px)',
        backgroundSize:'44px 44px',
      }}/>

      {/* Orbs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full pointer-events-none"
        style={{ background:'radial-gradient(circle,rgba(34,211,238,.13) 0%,transparent 70%)' }}/>
      <div className="absolute -bottom-16 -right-8 w-72 h-72 rounded-full pointer-events-none"
        style={{ background:`radial-gradient(circle,${current.accent}18 0%,transparent 70%)`, transition:'background 0.6s' }}/>

      {/* Scan line */}
      <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background:`linear-gradient(90deg,transparent,${current.accent}40,transparent)`, animation:'scan-h 4s linear infinite', opacity:.6 }}/>

      <div className="relative z-10 flex flex-col h-full px-10 py-7">

        {/* Top: Logo + badge */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl blur-sm" style={{ background:'rgba(34,211,238,0.55)', animation:'glow-breathe 3s ease-in-out infinite' }}/>
              <div className="relative w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background:'linear-gradient(135deg,#22d3ee,#7c3aed)' }}>
                <Infinity className="w-4 h-4 text-white" strokeWidth={2.5}/>
              </div>
            </div>
            <div>
              <p style={{ color:'#22d3ee', fontSize:9, fontWeight:900, textTransform:'uppercase', letterSpacing:'.18em' }}>SPX</p>
              <p style={{ color:'white', fontSize:12, fontWeight:700, lineHeight:1.2 }}>Payments Orchestration</p>
            </div>
          </div>
          {/* Screen badge */}
          <div className="px-2.5 py-1 rounded-lg" style={{ background:`${current.accent}15`, border:`1px solid ${current.accent}35`, transition:'all 0.4s' }}>
            <span style={{ color:current.accent, fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', transition:'color 0.4s' }}>
              {current.title}
            </span>
          </div>
        </div>

        {/* Main content — fading */}
        <div className="flex-1 min-h-0 overflow-hidden"
          style={{ opacity: fading ? 0 : 1, transform: fading ? 'translateY(8px)' : 'translateY(0)', transition:'opacity 0.32s ease, transform 0.32s ease' }}>
          <current.Comp />
        </div>

        {/* Bottom: dots + progress + stats */}
        <div className="mt-4">
          {/* Progress bar */}
          <div className="w-full h-px mb-4" style={{ background:'rgba(255,255,255,0.06)' }}>
            <div key={`${active}-bar`} style={{ height:'100%', background:current.accent, animation:`progress-bar ${INTERVAL_MS}ms linear forwards`, opacity:0.7 }}/>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {SCREENS.map((s, i) => (
              <button key={s.id} onClick={() => goTo(i)}
                className="transition-all duration-300"
                style={{
                  width: i === active ? 22 : 6,
                  height: 6,
                  borderRadius: 4,
                  background: i === active ? s.accent : 'rgba(255,255,255,0.12)',
                  boxShadow: i === active ? `0 0 8px ${s.accent}` : 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { val:'99.9%', lbl:'Uptime',   c:'#22d3ee' },
              { val:'10+',   lbl:'Gateways', c:'#a855f7' },
              { val:'<120ms',lbl:'Latency',  c:'#34d399' },
            ].map(s => (
              <div key={s.lbl} className="rounded-xl p-2.5 text-center" style={{ background:'rgba(255,255,255,0.025)', border:'1px solid rgba(255,255,255,0.05)' }}>
                <p style={{ color:s.c, fontSize:12, fontWeight:900 }}>{s.val}</p>
                <p style={{ color:'#1e3344', fontSize:8, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>{s.lbl}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RIGHT PANEL COMPONENTS
═══════════════════════════════════════════ */
function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.616z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022"/>
      <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00"/>
      <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF"/>
      <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900"/>
    </svg>
  );
}

function GlowBorder({ children }: { children: ReactNode }) {
  return (
    <div style={{ position:'relative', borderRadius:22, padding:1, overflow:'hidden' }}>
      <div style={{ position:'absolute', top:'50%', left:'50%', width:'200%', height:'200%', background:'conic-gradient(from 0deg at 50% 50%,#22d3ee 0deg,transparent 80deg,#a855f7 180deg,transparent 260deg,#22d3ee 360deg)', animation:'spin-border 6s linear infinite', opacity:.5 }}/>
      <div style={{ position:'relative', zIndex:1, borderRadius:21, background:'#06101e' }}>
        {children}
      </div>
    </div>
  );
}

const InputField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { icon: ElementType; error?: string }>(
function InputField({ icon: Icon, error, ...props }, ref) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-all duration-300"
        style={{ color: focused ? '#22d3ee' : '#1e3a4a' }}/>
      <input
        ref={ref}
        {...props}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
        className="w-full pl-10 pr-4 py-3 text-sm rounded-xl transition-all duration-300 focus:outline-none placeholder:text-slate-800"
        style={{
          background: focused ? 'rgba(34,211,238,0.05)' : 'rgba(255,255,255,0.03)',
          border: error ? '1px solid rgba(239,68,68,0.55)' : focused ? '1px solid rgba(34,211,238,0.5)' : '1px solid rgba(255,255,255,0.07)',
          boxShadow: focused && !error ? '0 0 0 3px rgba(34,211,238,0.1),inset 0 1px 0 rgba(34,211,238,0.06)' : error ? '0 0 0 3px rgba(239,68,68,0.07)' : 'none',
          color:'#e2e8f0', caretColor:'#22d3ee',
        }}
      />
    </div>
  );
});

/* ═══════════════════════════════════════════
   MAIN
═══════════════════════════════════════════ */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth  = useAuthStore((s) => s.setAuth);
  // Lets a caller (e.g. the Super Admin portal) send the user back to a
  // specific route after login instead of always landing on /dashboard.
  const next = new URLSearchParams(location.search).get('next') || '/dashboard';
  const [googleError,    setGoogleError]    = useState<string | null>(null);
  const [microsoftError, setMicrosoftError] = useState<string | null>(null);
  const [passkeyError,   setPasskeyError]   = useState<string | null>(null);
  const [loginStep,  setLoginStep]  = useState<'credentials' | 'totp' | 'setup-2fa' | 'reset-request' | 'forgot-password'>('credentials');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaHintIndex, setMfaHintIndex] = useState<number | null>(null);
  // Which provider triggered the current MFA challenge, so verifyMfaCode can log the right analytics event.
  const [mfaProvider, setMfaProvider] = useState<'email' | 'google' | 'microsoft'>('email');
  const [totpDigits,  setTotpDigits]  = useState(['', '', '', '', '', '']);
  const [totpError,   setTotpError]   = useState<string | null>(null);
  const [totpPending, setTotpPending] = useState(false);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Phone-factor sign-in state (only used when the resolved hint is 'phone')
  const [phoneVerificationId, setPhoneVerificationId] = useState<string | null>(null);
  const [phoneSending,        setPhoneSending]        = useState(false);
  const [phoneSendError,      setPhoneSendError]      = useState<string | null>(null);
  const loginRecaptchaRef = useRef<RecaptchaVerifier | null>(null);
  useEffect(() => () => { loginRecaptchaRef.current?.clear(); loginRecaptchaRef.current = null; }, []);

  // Setup-2FA state (mandatory enrollment during login)
  const [setupUser,     setSetupUser]     = useState<FirebaseUser | null>(null);
  const [setupPhase,    setSetupPhase]    = useState<'scan' | 'verify'>('scan');
  const [setupQrUrl,    setSetupQrUrl]    = useState<string>('');
  const [setupSecret,   setSetupSecret]   = useState<string>('');
  const [setupDigits,   setSetupDigits]   = useState(['', '', '', '', '', '']);
  const [setupError,    setSetupError]    = useState<string | null>(null);
  const [setupPending,  setSetupPending]  = useState(false);
  const [setupCancelling, setSetupCancelling] = useState(false);
  const setupDigitRefs  = useRef<(HTMLInputElement | null)[]>([]);

  // Reset-2FA state
  const [resetEmail,    setResetEmail]    = useState('');
  const [resetSent,     setResetSent]     = useState(false);
  const [resetPending,  setResetPending]  = useState(false);
  const [resetError,    setResetError]    = useState<string | null>(null);

  // Forgot-password state
  const [pwdResetEmail,   setPwdResetEmail]   = useState('');
  const [pwdResetSent,    setPwdResetSent]    = useState(false);
  const [pwdResetPending, setPwdResetPending] = useState(false);
  const [pwdResetError,   setPwdResetError]   = useState<string | null>(null);

  const { register, handleSubmit, setError, getValues, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema) });

  const loginMutation = useMutation({
    mutationFn: async (data: FormData) => {
      try {
        const cred = await signInWithEmailAndPassword(firebaseAuth, data.email, data.password);

        // Try mandatory 2FA enrollment — skip if TOTP not enabled in Firebase Console
        if (multiFactor(cred.user).enrolledFactors.length === 0) {
          try {
            const mfa = multiFactor(cred.user);
            const session = await mfa.getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            const uri = secret.generateQrCodeUrl(cred.user.email ?? 'user', 'SPX Payments Orchestration');
            const qrUrl = await QRCode.toDataURL(uri, { width: 200, color: { dark: '#22d3ee', light: '#050c18' } });
            setSetupUser(cred.user);
            setSetupSecret(secret.secretKey);
            setSetupQrUrl(qrUrl);
            setSetupPhase('scan');
            setLoginStep('setup-2fa');
            (cred.user as any).__totpSecret = secret;
            return null;
          } catch (mfaErr: any) {
            if (mfaErr.code !== 'auth/operation-not-allowed') throw mfaErr;
            // TOTP not enabled in Firebase Console — fall through to normal login
          }
        }

        const idToken = await cred.user.getIdToken();
        return (await api.post('/auth/firebase', { idToken })).data.data;
      } catch (err: any) {
        if (err.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(firebaseAuth, err);
          setMfaResolver(resolver);
          setMfaHintIndex(resolver.hints.length === 1 ? 0 : null);
          setMfaProvider('email');
          setLoginStep('totp');
          return null;
        }
        // Email/Password provider not enabled in Firebase Console — fall back to backend login
        if (err.code === 'auth/operation-not-allowed') {
          return (await api.post('/auth/login', { email: data.email, password: data.password })).data.data;
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      if (!data) return;
      logLogin('email');
      setAuth(data.user, data.accessToken, data.refreshToken);
      navigate(next);
    },
    onError: (err: any) => {
      const code = err.code ?? '';
      const msg = (code === 'auth/invalid-credential' || code === 'auth/user-not-found' || code === 'auth/wrong-password')
        ? 'Invalid email or password'
        : err.response?.data?.message || err.message || 'Sign-in failed';
      setError('root', { message: msg });
    },
  });

  const enrollTotp = async () => {
    if (!setupUser) return;
    const code = setupDigits.join('');
    if (code.length !== 6) return;
    setSetupPending(true);
    setSetupError(null);
    try {
      const secret = (setupUser as any).__totpSecret;
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(setupUser).enroll(assertion, 'Authenticator App');
      const idToken = await setupUser.getIdToken(true);
      const data = (await api.post('/auth/firebase', { idToken })).data.data;
      logLogin('email');
      setAuth(data.user, data.accessToken, data.refreshToken);
      navigate(next);
    } catch {
      setSetupError('Invalid code. Please check your authenticator and try again.');
      setSetupDigits(['', '', '', '', '', '']);
      setupDigitRefs.current[0]?.focus();
    } finally {
      setSetupPending(false);
    }
  };

  const handleSetupDigitChange = (i: number, val: string) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...setupDigits];
    next[i] = d;
    setSetupDigits(next);
    setSetupError(null);
    if (d && i < 5) setupDigitRefs.current[i + 1]?.focus();
    if (next.every(Boolean) && next.join('').length === 6) {
      setTimeout(() => enrollTotp(), 80);
    }
  };

  // Auto-focus the first code box the moment the verify phase appears
  useEffect(() => {
    if (loginStep === 'setup-2fa' && setupPhase === 'verify') {
      setupDigitRefs.current[0]?.focus();
    }
  }, [loginStep, setupPhase]);

  const cancelSetup = async () => {
    setSetupCancelling(true);
    try {
      if (setupUser) await signOut(firebaseAuth);
    } catch {
      // ignore — we're resetting local state regardless
    } finally {
      setSetupUser(null);
      setSetupPhase('scan');
      setSetupQrUrl('');
      setSetupSecret('');
      setSetupDigits(['', '', '', '', '', '']);
      setSetupError(null);
      setSetupCancelling(false);
      setLoginStep('credentials');
    }
  };

  const handleSetupDigitKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !setupDigits[i] && i > 0) setupDigitRefs.current[i - 1]?.focus();
  };

  const sendResetLink = async () => {
    if (!resetEmail) return;
    setResetPending(true);
    setResetError(null);
    try {
      await api.post('/auth/2fa/reset-request', { email: resetEmail });
      setResetSent(true);
    } catch (err: any) {
      setResetError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setResetPending(false);
    }
  };

  const sendPasswordResetLink = async () => {
    if (!pwdResetEmail) return;
    setPwdResetPending(true);
    setPwdResetError(null);
    try {
      await api.post('/auth/password-reset-request', { email: pwdResetEmail });
      setPwdResetSent(true);
    } catch (err: any) {
      setPwdResetError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setPwdResetPending(false);
    }
  };

  const selectedHint = mfaHintIndex !== null ? mfaResolver?.hints[mfaHintIndex] ?? null : null;

  const sendLoginPhoneCode = async (hintIndex: number) => {
    if (!mfaResolver) return;
    setPhoneSending(true);
    setPhoneSendError(null);
    try {
      const hint = mfaResolver.hints[hintIndex];
      const provider = new PhoneAuthProvider(firebaseAuth);
      if (!loginRecaptchaRef.current) {
        loginRecaptchaRef.current = new RecaptchaVerifier(firebaseAuth, LOGIN_RECAPTCHA_CONTAINER_ID, { size: 'invisible' });
      }
      const id = await provider.verifyPhoneNumber({ multiFactorHint: hint, session: mfaResolver.session }, loginRecaptchaRef.current);
      setPhoneVerificationId(id);
    } catch (err: any) {
      setPhoneSendError(err.message || 'Failed to send code. Please try again.');
      // Deliberately not clearing loginRecaptchaRef here — Firebase's invisible
      // verifier is meant to be reused across repeated verifyPhoneNumber() calls;
      // recreating it against the same container throws "reCAPTCHA has already
      // been rendered in this element."
    } finally {
      setPhoneSending(false);
    }
  };

  // Phone factors need an SMS sent before a code can be entered — TOTP doesn't.
  useEffect(() => {
    if (loginStep === 'totp' && selectedHint?.factorId === 'phone' && !phoneVerificationId && !phoneSending) {
      sendLoginPhoneCode(mfaHintIndex as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginStep, mfaHintIndex, selectedHint]);

  const verifyMfaCode = async () => {
    if (!mfaResolver || !selectedHint) return;
    const code = totpDigits.join('');
    if (code.length !== 6) return;
    setTotpPending(true);
    setTotpError(null);
    try {
      const assertion = selectedHint.factorId === 'phone'
        ? (() => {
            if (!phoneVerificationId) throw new Error('missing-verification-id');
            const phoneCred = PhoneAuthProvider.credential(phoneVerificationId, code);
            return PhoneMultiFactorGenerator.assertion(phoneCred);
          })()
        : TotpMultiFactorGenerator.assertionForSignIn(selectedHint.uid, code);
      const cred = await mfaResolver.resolveSignIn(assertion);
      const idToken = await cred.user.getIdToken();
      const data = (await api.post('/auth/firebase', { idToken })).data.data;
      logLogin(mfaProvider);
      setAuth(data.user, data.accessToken, data.refreshToken);
      navigate(next);
    } catch {
      setTotpError('Invalid code. Please try again.');
      setTotpDigits(['', '', '', '', '', '']);
      digitRefs.current[0]?.focus();
    } finally {
      setTotpPending(false);
    }
  };

  const handleDigitChange = (i: number, val: string) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...totpDigits];
    next[i] = d;
    setTotpDigits(next);
    setTotpError(null);
    if (d && i < 5) digitRefs.current[i + 1]?.focus();
    if (next.every(Boolean) && next.join('').length === 6) {
      setTimeout(() => {
        const code = next.join('');
        if (code.length === 6 && mfaResolver) verifyMfaCode();
      }, 80);
    }
  };

  const handleDigitKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !totpDigits[i] && i > 0) digitRefs.current[i - 1]?.focus();
  };
  // Popup-based providers throw the same auth/multi-factor-auth-required error as
  // email/password when the account has TOTP/phone MFA enrolled — route it into the
  // exact same resolver + code-entry step instead of surfacing it as a generic failure.
  const handlePopupMfaRequired = (err: any, provider: 'google' | 'microsoft'): boolean => {
    if (err.code !== 'auth/multi-factor-auth-required') return false;
    const resolver = getMultiFactorResolver(firebaseAuth, err);
    setMfaResolver(resolver);
    setMfaHintIndex(resolver.hints.length === 1 ? 0 : null);
    setMfaProvider(provider);
    setLoginStep('totp');
    return true;
  };

  const googleMutation = useMutation({
    mutationFn: async () => {
      if (isMobileAuthDevice()) {
        sessionStorage.setItem(PENDING_OAUTH_PROVIDER_KEY, 'google');
        await signInWithRedirect(firebaseAuth, googleProvider);
        return null; // browser is navigating away — result picked up after redirect
      }
      const r = await signInWithPopup(firebaseAuth, googleProvider);
      return (await api.post('/auth/firebase', { idToken: await r.user.getIdToken() })).data.data;
    },
    onSuccess: (data) => { if (!data) return; logLogin('google'); setAuth(data.user, data.accessToken, data.refreshToken); navigate(next); },
    onError:   (err: any) => {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      if (handlePopupMfaRequired(err, 'google')) return;
      setGoogleError(err.response?.data?.message || err.message || 'Google sign-in failed');
    },
  });
  const microsoftMutation = useMutation({
    mutationFn: async () => {
      if (isMobileAuthDevice()) {
        sessionStorage.setItem(PENDING_OAUTH_PROVIDER_KEY, 'microsoft');
        await signInWithRedirect(firebaseAuth, microsoftProvider);
        return null; // browser is navigating away — result picked up after redirect
      }
      const r = await signInWithPopup(firebaseAuth, microsoftProvider);
      return (await api.post('/auth/firebase', { idToken: await r.user.getIdToken() })).data.data;
    },
    onSuccess: (data) => { if (!data) return; logLogin('microsoft'); setAuth(data.user, data.accessToken, data.refreshToken); navigate(next); },
    onError:   (err: any) => {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      if (handlePopupMfaRequired(err, 'microsoft')) return;
      setMicrosoftError(err.response?.data?.message || err.message || 'Microsoft sign-in failed');
    },
  });

  // Completes the signInWithRedirect flow kicked off above on mobile: the
  // page reloaded after the provider round-trip, so pick the result back up
  // here using the provider we stashed just before redirecting.
  useEffect(() => {
    const provider = sessionStorage.getItem(PENDING_OAUTH_PROVIDER_KEY) as 'google' | 'microsoft' | null;
    if (!provider) return;
    sessionStorage.removeItem(PENDING_OAUTH_PROVIDER_KEY);

    getRedirectResult(firebaseAuth)
      .then(async (r) => {
        if (!r) return;
        const data = (await api.post('/auth/firebase', { idToken: await r.user.getIdToken() })).data.data;
        if (!data) return;
        logLogin(provider);
        setAuth(data.user, data.accessToken, data.refreshToken);
        navigate(next);
      })
      .catch((err: any) => {
        if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
        if (handlePopupMfaRequired(err, provider)) return;
        const message = err.response?.data?.message || err.message || `${provider === 'google' ? 'Google' : 'Microsoft'} sign-in failed`;
        if (provider === 'google') setGoogleError(message); else setMicrosoftError(message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const passkeyMutation = useMutation({
    mutationFn: loginWithPasskey,
    onSuccess: (data) => { logLogin('passkey'); setAuth(data.user, data.accessToken, data.refreshToken); navigate(next); },
    onError:   (err: any) => {
      const cancelled = err?.name === 'NotAllowedError' || err?.message?.toLowerCase?.().includes('cancel');
      if (cancelled) return;
      setPasskeyError(err.response?.data?.message || err.message || 'Passkey sign-in failed');
    },
  });

  const anyPending = googleMutation.isPending || microsoftMutation.isPending || passkeyMutation.isPending || loginMutation.isPending;

  return (
    <>
      <style>{STYLES}</style>
      <div className="h-screen flex" style={{ background:'#050c18' }}>

        {/* ── Left: 5-screen splash ── */}
        <div className="hidden lg:block lg:w-[58%]" style={{ height:'100vh', position:'sticky', top:0 }}>
          <SplashPanel />
        </div>

        {/* ── Right: Login ── */}
        <div className="lg:w-[42%] w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
          style={{ background:'#060e1c', borderLeft:'1px solid rgba(34,211,238,0.06)' }}>

          {/* Ambient */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full pointer-events-none"
            style={{ background:'radial-gradient(circle,rgba(34,211,238,.052) 0%,transparent 62%)' }}/>
          <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full pointer-events-none"
            style={{ background:'radial-gradient(circle,rgba(124,58,237,.05) 0%,transparent 65%)' }}/>

          {/* Particles */}
          {PARTICLES.map((p, i) => (
            <div key={i} className="absolute rounded-full pointer-events-none"
              style={{ left:p.l, top:p.t, width:p.s, height:p.s, background: i%3===0?'#22d3ee':i%3===1?'#a855f7':'#34d399', animation:`particle-rise ${p.dur} ${p.d} ease-in-out infinite`, opacity:0 }}/>
          ))}

          <div className="relative z-10 w-full max-w-[420px] px-8 py-6 max-h-full overflow-y-auto">
            <GlowBorder>
              <div className="px-8 py-6">

                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-lg blur-sm" style={{ background:'rgba(34,211,238,0.6)' }}/>
                    <div className="relative w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background:'linear-gradient(135deg,#22d3ee,#7c3aed)' }}>
                      <Infinity className="w-4 h-4 text-white" strokeWidth={2.5}/>
                    </div>
                  </div>
                  <div>
                    <p style={{ color:'#22d3ee', fontSize:9, fontWeight:900, textTransform:'uppercase', letterSpacing:'.2em' }}>SPX · Secure Access</p>
                    <p style={{ color:'white', fontSize:11, fontWeight:600, lineHeight:1.3 }}>Payments Orchestration Console</p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 rounded-lg w-fit"
                  style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.2)' }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background:'#34d399', display:'inline-block', animation:'pulse-dot 2s infinite' }}/>
                  <span style={{ color:'#34d399', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em' }}>All systems operational</span>
                </div>

                {loginStep === 'credentials' && (
                  <>
                    <h2 style={{ color:'white', fontSize:24, fontWeight:900, letterSpacing:'-.02em', lineHeight:1.1, marginBottom:3 }}>Welcome back</h2>
                    <p style={{ color:'rgba(148,218,255,0.55)', fontSize:12, marginBottom:16 }}>Sign in to continue to your console</p>

                    {/* Quick sign-in — one visual tier, no button competes for "hero" status */}
                    <div className="space-y-2 mb-2.5">
                      {browserSupportsWebAuthn() && (
                        <button type="button" disabled={anyPending}
                          onClick={() => { setPasskeyError(null); passkeyMutation.mutate(); }}
                          className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-40"
                          style={{ background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.22)', color:'#67e8f9' }}
                          onMouseEnter={(e) => { const el=e.currentTarget as HTMLElement; el.style.background='rgba(34,211,238,0.12)'; el.style.borderColor='rgba(34,211,238,0.4)'; }}
                          onMouseLeave={(e) => { const el=e.currentTarget as HTMLElement; el.style.background='rgba(34,211,238,0.06)'; el.style.borderColor='rgba(34,211,238,0.22)'; }}>
                          {passkeyMutation.isPending ? (
                            <>
                              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="rgba(103,232,249,0.3)" strokeWidth="3"/>
                                <path d="M12 2a10 10 0 0 1 10 10" stroke="#67e8f9" strokeWidth="3" strokeLinecap="round"/>
                              </svg>
                              Waiting for your device…
                            </>
                          ) : (
                            <><Fingerprint className="w-4 h-4"/> Sign in with a passkey</>
                          )}
                        </button>
                      )}

                      <div className="grid grid-cols-2 gap-2.5">
                        {[
                          { icon:<GoogleIcon/>,    label: googleMutation.isPending    ? '…' : 'Google',    onClick:() => { setGoogleError(null);    googleMutation.mutate();    }, hBg:'rgba(66,133,244,0.08)',  hBd:'rgba(66,133,244,0.45)', hSh:'0 0 18px rgba(66,133,244,0.2)'  },
                          { icon:<MicrosoftIcon/>, label: microsoftMutation.isPending ? '…' : 'Microsoft', onClick:() => { setMicrosoftError(null); microsoftMutation.mutate(); }, hBg:'rgba(0,164,239,0.08)',   hBd:'rgba(0,164,239,0.45)',  hSh:'0 0 18px rgba(0,164,239,0.2)'   },
                        ].map((btn) => (
                          <button key={btn.label} type="button" disabled={anyPending} onClick={btn.onClick}
                            className="flex items-center justify-center gap-2.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-40"
                            style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', color:'#64748b' }}
                            onMouseEnter={(e) => { const el=e.currentTarget as HTMLElement; el.style.background=btn.hBg; el.style.borderColor=btn.hBd; el.style.boxShadow=btn.hSh; el.style.color='#e2e8f0'; }}
                            onMouseLeave={(e) => { const el=e.currentTarget as HTMLElement; el.style.background='rgba(255,255,255,0.04)'; el.style.borderColor='rgba(255,255,255,0.08)'; el.style.boxShadow='none'; el.style.color='#64748b'; }}>
                            {btn.icon}<span>{btn.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {(passkeyError || googleError || microsoftError) && (
                      <div className="mb-3 px-3 py-2.5 rounded-xl text-xs flex items-start gap-2"
                        style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 mt-0.5"/>
                        {passkeyError || googleError || microsoftError}
                      </div>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1 h-px" style={{ background:'rgba(255,255,255,0.05)' }}/>
                      <span style={{ color:'rgba(148,218,255,0.4)', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.12em' }}>or continue with email</span>
                      <div className="flex-1 h-px" style={{ background:'rgba(255,255,255,0.05)' }}/>
                    </div>
                  </>
                )}

                {loginStep === 'totp' ? (
                  /* ── MFA verification step (TOTP or phone) ── */
                  mfaHintIndex === null && mfaResolver && mfaResolver.hints.length > 1 ? (
                    /* ── Choose which enrolled factor to use ── */
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.15)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(34,211,238,0.12)' }}>
                          <Shield className="w-4 h-4" style={{ color:'#22d3ee' }}/>
                        </div>
                        <div>
                          <p style={{ color:'#22d3ee', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em' }}>Two-Factor Auth</p>
                          <p style={{ color:'rgba(148,218,255,0.6)', fontSize:11, marginTop:1 }}>Choose a verification method</p>
                        </div>
                      </div>

                      {mfaResolver.hints.map((hint, i) => (
                        <button key={hint.uid} type="button" onClick={() => setMfaHintIndex(i)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200"
                          style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.1)' }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(34,211,238,0.08)')}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)')}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(34,211,238,0.1)' }}>
                            {hint.factorId === 'phone'
                              ? <Phone className="w-4 h-4" style={{ color:'#22d3ee' }}/>
                              : <Shield className="w-4 h-4" style={{ color:'#22d3ee' }}/>}
                          </div>
                          <div>
                            <p style={{ color:'white', fontSize:12, fontWeight:700 }}>
                              {hint.factorId === 'phone' ? 'Text message' : 'Authenticator app'}
                            </p>
                            <p style={{ color:'rgba(148,218,255,0.5)', fontSize:11 }}>
                              {hint.factorId === 'phone' ? (hint as any).phoneNumber ?? 'SMS code' : hint.displayName || 'TOTP code'}
                            </p>
                          </div>
                        </button>
                      ))}

                      <button type="button" onClick={() => { setLoginStep('credentials'); setMfaResolver(null); setMfaHintIndex(null); setTotpDigits(['','','','','','']); setTotpError(null); }}
                        className="w-full text-center text-xs py-1.5 transition-colors"
                        style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                        onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                        onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                        ← Back to sign in
                      </button>
                    </div>
                  ) : (
                    /* ── Code entry (TOTP immediately, phone after SMS is sent) ── */
                    <div className="space-y-5">
                      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.15)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(34,211,238,0.12)' }}>
                          {selectedHint?.factorId === 'phone'
                            ? <Phone className="w-4 h-4" style={{ color:'#22d3ee' }}/>
                            : <Shield className="w-4 h-4" style={{ color:'#22d3ee' }}/>}
                        </div>
                        <div>
                          <p style={{ color:'#22d3ee', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em' }}>Two-Factor Auth</p>
                          <p style={{ color:'rgba(148,218,255,0.6)', fontSize:11, marginTop:1 }}>
                            {selectedHint?.factorId === 'phone'
                              ? (phoneSending ? 'Sending your code…' : phoneVerificationId ? `Code sent to ${(selectedHint as any).phoneNumber ?? 'your phone'}` : 'Preparing to send your code…')
                              : 'Enter the 6-digit code from your authenticator app'}
                          </p>
                        </div>
                      </div>

                      {phoneSendError && (
                        <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                          style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"/>
                          {phoneSendError}
                        </div>
                      )}

                      {/* 6-digit OTP boxes */}
                      <div className="flex justify-center gap-2.5">
                        {totpDigits.map((d, i) => (
                          <input
                            key={i}
                            ref={(el) => { digitRefs.current[i] = el; }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={d}
                            disabled={selectedHint?.factorId === 'phone' && !phoneVerificationId}
                            onChange={(e) => handleDigitChange(i, e.target.value)}
                            onKeyDown={(e) => handleDigitKey(i, e)}
                            onFocus={(e) => e.target.select()}
                            className="w-11 h-13 text-center text-xl font-black rounded-xl transition-all duration-200 focus:outline-none disabled:opacity-40"
                            style={{
                              height: 52,
                              background: d ? 'rgba(34,211,238,0.1)' : 'rgba(255,255,255,0.03)',
                              border: totpError ? '1.5px solid rgba(239,68,68,0.55)' : d ? '1.5px solid rgba(34,211,238,0.5)' : '1.5px solid rgba(255,255,255,0.1)',
                              color: '#22d3ee',
                              caretColor: '#22d3ee',
                              boxShadow: d ? '0 0 12px rgba(34,211,238,0.15)' : 'none',
                            }}
                          />
                        ))}
                      </div>

                      {totpError && (
                        <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                          style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"/>
                          {totpError}
                        </div>
                      )}

                      <button onClick={verifyMfaCode} disabled={totpDigits.join('').length !== 6 || totpPending || (selectedHint?.factorId === 'phone' && !phoneVerificationId)}
                        className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white overflow-hidden transition-all duration-200 disabled:opacity-50"
                        style={{ background:'linear-gradient(135deg,#22d3ee 0%,#7c3aed 50%,#22d3ee 100%)', backgroundSize:'200% auto', animation: totpPending ? 'none' : 'shimmer 3s linear infinite', boxShadow:'0 4px 22px rgba(34,211,238,0.22)', letterSpacing:'.04em' }}>
                        <span className="absolute inset-0 pointer-events-none" style={{ background:'linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.11) 50%,transparent 60%)', backgroundSize:'200% auto', animation:'shimmer 2.5s linear infinite' }}/>
                        {totpPending ? (
                          <span className="relative flex items-center gap-2">
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3"/>
                              <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                            Verifying…
                          </span>
                        ) : (
                          <span className="relative flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/> Verify Code</span>
                        )}
                      </button>

                      <div className="flex flex-col items-center gap-1.5">
                        {selectedHint?.factorId === 'phone' && (
                          <button type="button" onClick={() => sendLoginPhoneCode(mfaHintIndex as number)} disabled={phoneSending}
                            className="text-xs py-1 transition-colors disabled:opacity-50"
                            style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                            {phoneSending ? 'Resending…' : 'Resend code'}
                          </button>
                        )}
                        {mfaResolver && mfaResolver.hints.length > 1 && (
                          <button type="button" onClick={() => { setMfaHintIndex(null); setPhoneVerificationId(null); setTotpDigits(['','','','','','']); setTotpError(null); setPhoneSendError(null); }}
                            className="text-xs py-1 transition-colors"
                            style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                            Use a different method
                          </button>
                        )}
                        <button type="button" onClick={() => { setLoginStep('reset-request'); setResetEmail(''); setResetSent(false); setResetError(null); }}
                          className="text-xs py-1 transition-colors"
                          style={{ color:'rgba(148,218,255,0.38)', background:'none', border:'none', cursor:'pointer' }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = '#22d3ee')}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.38)')}>
                          Can't access your authenticator?
                        </button>
                        <button type="button" onClick={() => { setLoginStep('credentials'); setMfaResolver(null); setMfaHintIndex(null); setPhoneVerificationId(null); setTotpDigits(['','','','','','']); setTotpError(null); }}
                          className="text-xs py-1 transition-colors"
                          style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                          ← Back to sign in
                        </button>
                      </div>
                    </div>
                  )
                ) : loginStep === 'reset-request' ? (
                  /* ── 2FA reset request step ── */
                  <div className="space-y-5">
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.2)' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(168,85,247,0.12)' }}>
                        <KeyRound className="w-4 h-4" style={{ color:'#a855f7' }}/>
                      </div>
                      <div>
                        <p style={{ color:'#a855f7', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em' }}>Reset Two-Factor Auth</p>
                        <p style={{ color:'rgba(148,218,255,0.6)', fontSize:11, marginTop:1 }}>
                          {resetSent ? 'Check your inbox for a reset link.' : 'Enter your email to receive a reset link (expires in 15 min).'}
                        </p>
                      </div>
                    </div>

                    {!resetSent && (
                      <>
                        <InputField
                          icon={Mail}
                          type="email"
                          placeholder="your@email.com"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendResetLink()}
                        />
                        {resetError && (
                          <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                            style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"/>
                            {resetError}
                          </div>
                        )}
                        <button onClick={sendResetLink} disabled={!resetEmail || resetPending}
                          className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white transition-all duration-200 disabled:opacity-50"
                          style={{ background:'linear-gradient(135deg,#a855f7 0%,#7c3aed 100%)', boxShadow:'0 4px 22px rgba(168,85,247,0.22)', letterSpacing:'.04em' }}>
                          {resetPending ? 'Sending…' : 'Send Reset Link'}
                        </button>
                      </>
                    )}

                    {resetSent && (
                      <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                        style={{ background:'rgba(52,211,153,0.07)', border:'1px solid rgba(52,211,153,0.2)', color:'#6ee7b7' }}>
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0"/>
                        Reset link sent! Check your email and follow the instructions.
                      </div>
                    )}

                    <button type="button" onClick={() => { setLoginStep('totp'); setResetEmail(''); setResetSent(false); setResetError(null); }}
                      className="w-full text-center text-xs py-1.5 transition-colors"
                      style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                      ← Back to verification
                    </button>
                  </div>
                ) : loginStep === 'forgot-password' ? (
                  /* ── Forgot password step ── */
                  <div className="space-y-5">
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.15)' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(34,211,238,0.12)' }}>
                        <Lock className="w-4 h-4" style={{ color:'#22d3ee' }}/>
                      </div>
                      <div>
                        <p style={{ color:'#22d3ee', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em' }}>Reset Password</p>
                        <p style={{ color:'rgba(148,218,255,0.6)', fontSize:11, marginTop:1 }}>
                          {pwdResetSent ? 'Check your inbox for a reset link.' : 'Enter your email to receive a reset link (expires in 15 min).'}
                        </p>
                      </div>
                    </div>

                    {!pwdResetSent && (
                      <>
                        <InputField
                          icon={Mail}
                          type="email"
                          placeholder="your@email.com"
                          value={pwdResetEmail}
                          onChange={(e) => setPwdResetEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendPasswordResetLink()}
                        />
                        {pwdResetError && (
                          <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                            style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"/>
                            {pwdResetError}
                          </div>
                        )}
                        <button onClick={sendPasswordResetLink} disabled={!pwdResetEmail || pwdResetPending}
                          className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white transition-all duration-200 disabled:opacity-50"
                          style={{ background:'linear-gradient(135deg,#22d3ee 0%,#7c3aed 100%)', boxShadow:'0 4px 22px rgba(34,211,238,0.22)', letterSpacing:'.04em' }}>
                          {pwdResetPending ? 'Sending…' : 'Send Reset Link'}
                        </button>
                      </>
                    )}

                    {pwdResetSent && (
                      <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                        style={{ background:'rgba(52,211,153,0.07)', border:'1px solid rgba(52,211,153,0.2)', color:'#6ee7b7' }}>
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0"/>
                        Reset link sent! Check your email and follow the instructions.
                      </div>
                    )}

                    <button type="button" onClick={() => { setLoginStep('credentials'); setPwdResetEmail(''); setPwdResetSent(false); setPwdResetError(null); }}
                      className="w-full text-center text-xs py-1.5 transition-colors"
                      style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                      ← Back to sign in
                    </button>
                  </div>
                ) : loginStep === 'setup-2fa' ? (
                  /* ── Mandatory 2FA setup (first login) ── */
                  <div className="space-y-5" style={{ animation:'fade-slide-up 0.35s both' }}>
                    <div className="flex items-start justify-between gap-3 p-3 rounded-xl" style={{ background:'rgba(34,211,238,0.06)', border:'1px solid rgba(34,211,238,0.18)' }}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background:'rgba(34,211,238,0.12)' }}>
                          <ScanLine className="w-4 h-4" style={{ color:'#22d3ee' }}/>
                        </div>
                        <div>
                          <p style={{ color:'#22d3ee', fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'.1em' }}>Secure Your Account</p>
                          <p style={{ color:'rgba(148,218,255,0.6)', fontSize:11, marginTop:1 }}>Two-factor authentication is required for every account before you can continue.</p>
                        </div>
                      </div>
                      <span className="shrink-0 px-2 py-1 rounded-md" style={{ background:'rgba(34,211,238,0.1)', border:'1px solid rgba(34,211,238,0.2)', color:'#22d3ee', fontSize:9, fontWeight:800, letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                        STEP {setupPhase === 'scan' ? '1' : '2'} / 2
                      </span>
                    </div>

                    {setupPhase === 'scan' ? (
                      <div className="space-y-4" style={{ animation:'fade-slide-up 0.3s both' }}>
                        {setupQrUrl && (
                          <div className="flex flex-col items-center gap-3">
                            <div className="p-3 rounded-xl" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(34,211,238,0.16)', boxShadow:'0 0 24px rgba(34,211,238,0.08)' }}>
                              <img src={setupQrUrl} alt="TOTP QR Code" style={{ width:168, height:168, display:'block', borderRadius:6 }}/>
                            </div>
                            <div className="w-full px-3 py-2 rounded-lg flex items-center justify-between gap-2"
                              style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)' }}>
                              <span style={{ color:'rgba(148,218,255,0.5)', fontSize:10, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', flexShrink:0 }}>Key</span>
                              <span style={{ color:'#22d3ee', fontSize:10, fontWeight:600, fontFamily:'monospace', letterSpacing:'.08em', wordBreak:'break-all' }}>{setupSecret}</span>
                              <button type="button" onClick={() => navigator.clipboard.writeText(setupSecret).catch(() => undefined)}
                                style={{ color:'rgba(148,218,255,0.4)', fontSize:9, background:'none', cursor:'pointer', flexShrink:0, padding:'2px 6px', borderRadius:4, border:'1px solid rgba(148,218,255,0.15)' }}>
                                Copy
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-2 px-1">
                          <Smartphone className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color:'rgba(148,218,255,0.4)' }}/>
                          <p style={{ color:'rgba(148,218,255,0.45)', fontSize:10.5, lineHeight:1.5 }}>
                            New to this? Install <strong style={{ color:'rgba(148,218,255,0.65)' }}>Google Authenticator</strong>, <strong style={{ color:'rgba(148,218,255,0.65)' }}>Authy</strong>, or <strong style={{ color:'rgba(148,218,255,0.65)' }}>1Password</strong>, then scan the code above.
                          </p>
                        </div>

                        <button onClick={() => setSetupPhase('verify')} disabled={!setupQrUrl}
                          className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white transition-all duration-200 disabled:opacity-50"
                          style={{ background:'linear-gradient(135deg,#22d3ee 0%,#7c3aed 100%)', boxShadow:'0 4px 22px rgba(34,211,238,0.22)', letterSpacing:'.04em' }}>
                          <span className="flex items-center gap-2">I've scanned it <ArrowRight className="w-4 h-4"/></span>
                        </button>

                        <div className="flex justify-center">
                          <button type="button" onClick={cancelSetup} disabled={setupCancelling}
                            className="text-xs py-1 transition-colors disabled:opacity-50"
                            style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                            {setupCancelling ? 'Signing out…' : 'Cancel and sign out'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4" style={{ animation:'fade-slide-up 0.3s both' }}>
                        <p style={{ color:'rgba(148,218,255,0.55)', fontSize:11, textAlign:'center' }}>Enter the 6-digit code from your authenticator to confirm setup</p>

                        <div className="flex justify-center gap-2.5">
                          {setupDigits.map((d, i) => (
                            <input
                              key={i}
                              ref={(el) => { setupDigitRefs.current[i] = el; }}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={d}
                              aria-label={`Digit ${i + 1} of 6`}
                              onChange={(e) => handleSetupDigitChange(i, e.target.value)}
                              onKeyDown={(e) => handleSetupDigitKey(i, e)}
                              onFocus={(e) => e.target.select()}
                              className="text-center text-xl font-black rounded-xl transition-all duration-200 focus:outline-none"
                              style={{
                                width: 44, height: 52,
                                background: d ? 'rgba(34,211,238,0.1)' : 'rgba(255,255,255,0.03)',
                                border: setupError ? '1.5px solid rgba(239,68,68,0.55)' : d ? '1.5px solid rgba(34,211,238,0.5)' : '1.5px solid rgba(255,255,255,0.1)',
                                color: '#22d3ee', caretColor: '#22d3ee',
                                boxShadow: d ? '0 0 12px rgba(34,211,238,0.15)' : 'none',
                              }}
                            />
                          ))}
                        </div>

                        {setupError && (
                          <div className="px-3 py-2.5 rounded-xl text-xs flex items-center gap-2"
                            style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"/>
                            {setupError}
                          </div>
                        )}

                        <button onClick={enrollTotp} disabled={setupDigits.join('').length !== 6 || setupPending}
                          className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white transition-all duration-200 disabled:opacity-50"
                          style={{ background:'linear-gradient(135deg,#22d3ee 0%,#7c3aed 100%)', boxShadow:'0 4px 22px rgba(34,211,238,0.22)', letterSpacing:'.04em' }}>
                          {setupPending ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3"/>
                                <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                              </svg>
                              Activating…
                            </span>
                          ) : (
                            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/> Activate 2FA & Sign In</span>
                          )}
                        </button>

                        <div className="flex flex-col items-center gap-1.5">
                          <button type="button" onClick={() => { setSetupPhase('scan'); setSetupError(null); setSetupDigits(['','','','','','']); }}
                            className="text-xs py-1 transition-colors"
                            style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                            ← Back to QR code
                          </button>
                          <button type="button" onClick={cancelSetup} disabled={setupCancelling}
                            className="text-xs py-1 transition-colors disabled:opacity-50"
                            style={{ color:'rgba(148,218,255,0.4)', background:'none', border:'none', cursor:'pointer' }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.7)')}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,218,255,0.4)')}>
                            {setupCancelling ? 'Signing out…' : 'Cancel and sign out'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── Credentials step ── */
                  <>
                    {errors.root && (
                      <div className="mb-3 px-3 py-2.5 rounded-xl text-xs flex items-start gap-2"
                        style={{ background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.18)', color:'#fca5a5' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 mt-0.5"/>
                        {errors.root.message}
                      </div>
                    )}

                    <form onSubmit={handleSubmit((d) => loginMutation.mutate(d))} className="space-y-3">
                      <div className="space-y-1.5">
                        <label style={{ display:'block', color:'rgba(148,218,255,0.6)', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'.12em' }}>Email address</label>
                        <InputField icon={Mail} type="email" placeholder="you@company.com" error={errors.email?.message} {...register('email')}/>
                        {errors.email && <p style={{ color:'#f87171', fontSize:11 }}>{errors.email.message}</p>}
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label style={{ color:'rgba(148,218,255,0.6)', fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'.12em' }}>Password</label>
                          <button type="button"
                            onClick={() => { setPwdResetEmail(getValues('email') || ''); setPwdResetSent(false); setPwdResetError(null); setLoginStep('forgot-password'); }}
                            style={{ color:'rgba(34,211,238,0.35)', fontSize:10, fontWeight:600, background:'none', border:'none', cursor:'pointer', transition:'color .2s' }}
                            onMouseEnter={(e)=>{ (e.currentTarget as HTMLElement).style.color='#22d3ee'; }}
                            onMouseLeave={(e)=>{ (e.currentTarget as HTMLElement).style.color='rgba(34,211,238,0.35)'; }}>
                            Forgot password?
                          </button>
                        </div>
                        <InputField icon={Lock} type="password" placeholder="••••••••" error={errors.password?.message} {...register('password')}/>
                        {errors.password && <p style={{ color:'#f87171', fontSize:11 }}>{errors.password.message}</p>}
                      </div>

                      <button type="submit" disabled={anyPending}
                        className="relative w-full flex items-center justify-center gap-2.5 py-3 rounded-xl text-sm font-black text-white overflow-hidden transition-all duration-200 disabled:opacity-50"
                        style={{ background:'linear-gradient(135deg,#22d3ee 0%,#7c3aed 50%,#22d3ee 100%)', backgroundSize:'200% auto', animation: loginMutation.isPending ? 'none' : 'shimmer 3s linear infinite', boxShadow:'0 4px 22px rgba(34,211,238,0.22),inset 0 1px 0 rgba(255,255,255,0.1)', letterSpacing:'.04em' }}
                        onMouseEnter={(e)=>{ if(!loginMutation.isPending)(e.currentTarget as HTMLElement).style.boxShadow='0 6px 32px rgba(34,211,238,0.42),inset 0 1px 0 rgba(255,255,255,0.1)'; }}
                        onMouseLeave={(e)=>{ (e.currentTarget as HTMLElement).style.boxShadow='0 4px 22px rgba(34,211,238,0.22),inset 0 1px 0 rgba(255,255,255,0.1)'; }}>
                        <span className="absolute inset-0 pointer-events-none" style={{ background:'linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.11) 50%,transparent 60%)', backgroundSize:'200% auto', animation:'shimmer 2.5s linear infinite' }}/>
                        {loginMutation.isPending ? (
                          <span className="relative flex items-center gap-2">
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3"/>
                              <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                            Authenticating…
                          </span>
                        ) : (
                          <span className="relative flex items-center gap-2">Sign In <ArrowRight className="w-4 h-4"/></span>
                        )}
                      </button>
                    </form>
                  </>
                )}

                {/* Footer */}
                <div className="mt-4 pt-4" style={{ borderTop:'1px solid rgba(255,255,255,0.04)' }}>
                  <div className="flex items-center justify-center gap-4">
                    {[
                      { I:Shield,       l:'256-bit TLS'   },
                      { I:CheckCircle2, l:'SOC 2 Type II' },
                      { I:CheckCircle2, l:'Zero retention' },
                    ].map(({ I, l }) => (
                      <div key={l} className="flex items-center gap-1.5">
                        <I className="w-3 h-3" style={{ color:'#22d3ee' }}/>
                        <span style={{ color:'rgba(148,218,255,0.7)', fontSize:8.5, fontWeight:600, textTransform:'uppercase', letterSpacing:'.08em' }}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Required by Firebase's invisible reCAPTCHA for phone-factor sign-in */}
                <div id={LOGIN_RECAPTCHA_CONTAINER_ID}/>

              </div>
            </GlowBorder>
          </div>
        </div>
      </div>
    </>
  );
}
